import React, { useState, useEffect } from "react";
import { auth } from "../../firebase";
import { FAMILY_RELATIONSHIPS } from "../../lib/familyRelationships";
import { ROLES } from "../../lib/rolesConfig";

const API_BASE_URL = `https://asia-southeast1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api`;

const EditMemberModal = ({ member, isOpen, onClose, activeCareProfileId }) => {
  const [newRole, setNewRole] = useState("");
  const [newRelationship, setNewRelationship] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (member) {
      setNewRole(member.role);
      setNewRelationship(member.relationship || "");
    }
  }, [member]);

  if (!isOpen || !member) {
    return null;
  }

  const handleMemberChange = async (event) => {
    event.preventDefault();
    if (!newRole || !newRelationship) return;
    setIsSaving(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User not logged in.");
      }

      const response = await fetch(
        `${API_BASE_URL}/care-team/update-team-member`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${await currentUser.getIdToken()}`,
          },
          body: JSON.stringify({
            careProfileId: activeCareProfileId,
            memberId: member.id,
            relationship: newRelationship,
            role: newRole,
          }),
        },
      );

      if (!response.ok) {
        const responseText = await response.text();
        let errorBody = {};
        try {
          errorBody = responseText ? JSON.parse(responseText) : {};
        } catch {
          errorBody = {};
        }

        if (response.status === 404) {
          throw new Error(
            "The member update service is not available yet. Deploy the Cloud Functions API, then try again.",
          );
        }

        throw new Error(
          errorBody.error ||
            errorBody.message ||
            `Failed to update member. Server returned ${response.status}.`,
        );
      }

      onClose();
    } catch (error) {
      console.error("Error updating family circle member: ", error);
      window.alert(`Failed to update member: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex h-full w-full items-center justify-center overflow-y-auto bg-gray-600 bg-opacity-50 p-4">
      <div className="relative mx-auto w-full max-w-md rounded-2xl border bg-white p-6 shadow-lg sm:p-8">
        <h3 className="text-2xl font-bold text-gray-800 mb-6">
          Edit Family Circle Member
        </h3>
        <form onSubmit={handleMemberChange}>
          <div className="mb-4">
            <p className="block text-sm font-medium text-gray-700 mb-1">
              Member
            </p>
            <p className="text-lg font-semibold text-gray-900">{member.name}</p>
            <p className="text-sm text-gray-500">{member.email}</p>
          </div>

          <div className="mb-4">
            <label
              htmlFor="relationship"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Assign relationship
            </label>
            <select
              id="relationship"
              name="relationship"
              value={newRelationship}
              onChange={(e) => setNewRelationship(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select relationship</option>
              {FAMILY_RELATIONSHIPS.map((relationship) => (
                <option key={relationship} value={relationship}>
                  {relationship}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label
              htmlFor="role"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Assign new role
            </label>
            <select
              id="role"
              name="role"
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.values(ROLES)
                .filter((role) => role !== ROLES.OWNER)
                .map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
            </select>
          </div>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="min-h-11 px-6 py-2 border rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isSaving ||
                (newRole === member.role &&
                  newRelationship === (member.relationship || ""))
              }
              className="min-h-11 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMemberModal;
